import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '@/components/shadcn-ui/button';
import { ScrollArea } from '@/components/shadcn-ui/scroll-area';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/useUser.store';
import { Home, Menu, X, Workflow, Building2, LogOut, LayoutDashboard } from 'lucide-react';

const navigationItems = [
  { icon: Home, label: 'Home', path: '/admin/automation' },
  { icon: Building2, label: 'AHJs', path: '/admin/automation/ahjs' },
];

const AdminAutomationLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const adminDetails = useUserStore(state => state.adminDetails);
  const { logoutAdmin } = useUserStore();

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/auth');
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-2 border-b border-border px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
          <Workflow className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold">Automation Engine</p>
          <p className="text-xs text-muted-foreground">Admin</p>
        </div>
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navigationItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-background-secondary text-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="space-y-2 border-t border-border p-4">
        <div className="rounded-md bg-muted/50 p-3">
          <p className="truncate text-sm font-medium">{adminDetails?.fullname || 'Admin'}</p>
          <p className="text-xs text-muted-foreground">
            {adminDetails?.superAdmin ? 'Super Admin' : 'Admin'}
          </p>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => navigate('/admin/dashboard')}
        >
          <LayoutDashboard className="mr-2 h-4 w-4" />
          Go To Dashboard
        </Button>
        <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted w-full">
      <div className="flex h-screen overflow-hidden">
        <aside className="hidden w-64 border-r border-border bg-card lg:block">
          <SidebarContent />
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 h-full w-64 border-r border-border bg-card">
              <SidebarContent />
            </aside>
          </div>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="flex h-16 items-center gap-4 border-b border-border bg-card px-6">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex flex-1 items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold">Automation</h1>
                <p className="text-xs text-muted-foreground">Author and maintain AHJ profiles</p>
              </div>
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto bg-muted p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminAutomationLayout;
